import { Box, Stack } from "@mui/joy";
import { useState } from "react";
import { classicyToast } from "../components/toast/ClassicyToast";
import { api } from "../../../../interceptors/api";
import { ClassicyButton } from "../components/button/ClassicyButton";
import { ClassicyControlGroup } from "../components/control-group/ClassicyControlGroup";
import ClassicyProgressBar from "../components/progress-bar/ClassicyProgressBar";
import ColorSelectionStep from "./steps/ColorSelectionStep";
import LocationSelectionStep from "./steps/LocationSelectionStep";
import ActivitySelectionStep from "./steps/ActivitySelectionStep";
import EmotionSelectionStep from "./steps/EmotionSelectionStep";
import AdditionalTextStep from "./steps/AdditionalTextStep";
import { useUserStories } from "../context/UserStoriesContext";
import "./styles/QuestionnaireWizard.css";

interface QuestionnaireState {
  color: string | null;
  location: string | null;
  locationOther: string;
  activities: string[];
  emotions: string[];
  additionalText: string;
  audioUrl: string;
  language: string;
}

const EXHIBITION_ID = 4;
const DEVICE_ID = 4; // iMac G3

function QuestionnaireWizard() {
  const { loadStories } = useUserStories();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [state, setState] = useState<QuestionnaireState>({
    color: null,
    location: null,
    locationOther: "",
    activities: [],
    emotions: [],
    additionalText: "",
    audioUrl: "",
    language: "de",
  });

  const totalSteps = 5;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return state.color !== null;
      case 2:
        if (state.location === "Other") {
          return state.locationOther.trim() !== "";
        }
        return state.location !== null;
      case 3:
        return state.activities.length > 0;
      case 4:
        return state.emotions.length > 0;
      case 5:
        return true; // Additional text is optional
      default:
        return false;
    }
  };

  const generateStoryText = (): string => {
    const {
      color,
      location,
      locationOther,
      activities,
      emotions,
      additionalText,
    } = state;

    // Format location
    const locationText =
      location === "Other" ? locationOther : location?.toLowerCase();

    // Format activities list (German)
    const activitiesList =
      activities.length === 1
        ? activities[0].toLowerCase()
        : activities.length === 2
          ? `${activities[0].toLowerCase()} and ${activities[1].toLowerCase()}`
          : `${activities.slice(0, -1).join(", ").toLowerCase()} and ${activities[activities.length - 1].toLowerCase()}`;

    // Format emotions list (German)
    const emotionsList =
      emotions.length === 1
        ? emotions[0].toLowerCase()
        : emotions.length === 2
          ? `${emotions[0].toLowerCase()} and ${emotions[1].toLowerCase()}`
          : `${emotions.slice(0, -1).join(", ").toLowerCase()} and ${emotions[emotions.length - 1].toLowerCase()}`;

    // Generate story template (German)
    let story = `I remember the iMac G3 in the color '${color}'. I used it at ${locationText} for ${activitiesList}. It made me feel ${emotionsList}.`;

    // Add additional text if provided
    if (additionalText.trim()) {
      story += ` ${additionalText.trim()}`;
    }

    return story;
  };

  const handleSubmit = async () => {
    if (!isStepValid()) {
      classicyToast.error("Bitte fülle alle erforderlichen Felder aus");
      return;
    }

    setIsSubmitting(true);

    const storyText = generateStoryText();

    try {
      const res = await api.post(
        `/api/user-story/submit-text/device/${DEVICE_ID}/exhibition/${EXHIBITION_ID}`,
        {
          storyText,
          originalLanguage: state.language,
          exhibitionId: EXHIBITION_ID,
          audioUrl: state.audioUrl,
        },
      );

      if (res.data.statusCode === 200) {
        classicyToast.success("Danke, dass du deine Geschichte geteilt hast!");

        // Reload user stories to include the newly submitted story
        await loadStories();

        // Reset form
        setState({
          color: null,
          location: null,
          locationOther: "",
          activities: [],
          emotions: [],
          additionalText: "",
          audioUrl: "",
          language: "",
        });
        setCurrentStep(1);
      } else {
        classicyToast.error(
          res.data.message || "Fehler beim Einreichen der Geschichte",
        );
      }
    } catch (err: any) {
      console.error("Error submitting story:", err);
      classicyToast.error(
        "Fehler beim Einreichen der Geschichte. Bitte versuche es erneut.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ColorSelectionStep
            selectedColor={state.color}
            onSelect={(color) => setState({ ...state, color })}
          />
        );
      case 2:
        return (
          <LocationSelectionStep
            selectedLocation={state.location}
            locationOther={state.locationOther}
            onSelect={(location) => setState({ ...state, location })}
            onOtherChange={(locationOther) =>
              setState({ ...state, locationOther })
            }
          />
        );
      case 3:
        return (
          <ActivitySelectionStep
            selectedActivities={state.activities}
            onToggle={(activity) => {
              const activities = state.activities.includes(activity)
                ? state.activities.filter((a) => a !== activity)
                : [...state.activities, activity];
              setState({ ...state, activities });
            }}
          />
        );
      case 4:
        return (
          <EmotionSelectionStep
            selectedEmotions={state.emotions}
            onToggle={(emotion) => {
              const emotions = state.emotions.includes(emotion)
                ? state.emotions.filter((e) => e !== emotion)
                : [...state.emotions, emotion];
              setState({ ...state, emotions });
            }}
          />
        );
      case 5:
        return (
          <AdditionalTextStep
            onChange={(additionalText, language, audioUrl) =>
              setState({ ...state, additionalText, audioUrl, language })
            }
            onRecordingChange={setIsRecording}
            exhibitionId={EXHIBITION_ID}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      className="questionnaire-wizard"
    >
      <Stack
        spacing={2}
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
          justifyContent: "space-between",
          padding: "0",
        }}
      >
        <ClassicyControlGroup
          label={`Schritt ${currentStep} von ${totalSteps}`}
          columns={false}
        >
          <ClassicyProgressBar value={currentStep} max={totalSteps} />
        </ClassicyControlGroup>

        {renderStep()}

        <div className="button-row">
          <ClassicyButton
            buttonType="reset"
            onClickFunc={handleBack}
            disabled={currentStep === 1 || isSubmitting}
          >
            Zurück
          </ClassicyButton>

          {currentStep < totalSteps ? (
            <ClassicyButton
              buttonType="button"
              onClickFunc={handleNext}
              disabled={!isStepValid() || isSubmitting}
            >
              Weiter
            </ClassicyButton>
          ) : (
            <ClassicyButton
              buttonType="submit"
              onClickFunc={handleSubmit}
              disabled={isSubmitting || isRecording}
            >
              {isSubmitting ? "Wird gesendet..." : "Geschichte einreichen"}
            </ClassicyButton>
          )}
        </div>
      </Stack>
    </Box>
  );
}

export default QuestionnaireWizard;
